import React, { useEffect, useState } from "react";
import {
  Box, Typography, Grid, Button, TextField, Select, MenuItem,
  Dialog, CircularProgress, IconButton, Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import { useNotifier } from "../../../Core/Notifier";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

const REASONS = [
  { value: "count_correction", label: "Count Correction" },
  { value: "damaged", label: "Damaged Goods" },
  { value: "expired", label: "Expired" },
  { value: "theft", label: "Theft / Loss" },
  { value: "returned", label: "Returned Stock" },
  { value: "other", label: "Other" },
];

export interface AdjustableProduct {
  id: number;
  productName: string;
  current_stock?: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  /** All selectable products (used when no defaultProduct is supplied) */
  products?: AdjustableProduct[];
  /** Pre-selected product — disables the product dropdown */
  defaultProduct?: AdjustableProduct | null;
  /** Called after a successful adjustment so the parent can refetch */
  onSuccess?: () => void;
}

const StockAdjustmentDialog: React.FC<Props> = ({ open, onClose, products = [], defaultProduct, onSuccess }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    product: null as AdjustableProduct | null,
    type: "deduct" as "add" | "deduct",
    qty: "",
    reason: "count_correction",
    notes: "",
  });

  // Reset form whenever the dialog opens / defaultProduct changes
  useEffect(() => {
    if (open) {
      setForm({
        product: defaultProduct || null,
        type: "deduct",
        qty: "",
        reason: "count_correction",
        notes: "",
      });
    }
  }, [open, defaultProduct]);

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  const handleSubmit = async () => {
    if (!form.product || !form.qty || Number(form.qty) <= 0) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/warehouse/product/${form.product.id}/adjust`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ type: form.type, qty: Number(form.qty), reason: form.reason, notes: form.notes }),
      });
      const json = await res.json();
      if (json.success) {
        showMessage(`Stock ${form.type === "add" ? "added" : "deducted"} successfully`);
        onClose();
        onSuccess?.();
      } else {
        showErrorMessage(json.message || "Failed");
      }
    } catch {
      showErrorMessage("Error adjusting stock");
    } finally {
      setSaving(false);
    }
  };

  const productLocked = !!defaultProduct;
  const currentStock = form.product?.current_stock ?? 0;

  // Always merge defaultProduct into the options list so the Autocomplete value is resolvable
  const mergedOptions = React.useMemo(() => {
    const base = Array.isArray(products) ? [...products] : [];
    if (defaultProduct && !base.some((p) => p.id === defaultProduct.id)) {
      base.unshift(defaultProduct);
    }
    return base;
  }, [products, defaultProduct]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disableEnforceFocus
      sx={{ zIndex: 1300 }}
      PaperProps={{ sx: { borderRadius: 3, overflow: "visible" } }}
    >
      <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #E5E7EB", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography fontSize={16} fontWeight={600} color="#111827">New Stock Adjustment</Typography>
        <IconButton size="small" onClick={onClose}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
      </Box>
      <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
        {/* Product selector */}
        <Box>
          <Typography fontSize={12} fontWeight={500} color="#6B7280" mb={0.5}>Product</Typography>
          <Autocomplete
            size="small"
            options={mergedOptions}
            value={form.product}
            disabled={false}
            disablePortal={false}
            openOnFocus
            autoHighlight
            blurOnSelect
            getOptionLabel={(o: any) => o?.productName || ""}
            isOptionEqualToValue={(opt: any, val: any) => opt?.id === val?.id}
            onChange={(_, v) => setForm(f => ({ ...f, product: v }))}
            noOptionsText={mergedOptions.length === 0 ? "No products available" : "No matches"}
            slotProps={{
              popper: { sx: { zIndex: 2000 }, modifiers: [{ name: "preventOverflow", enabled: true }] },
              paper:  { sx: { zIndex: 2000, pointerEvents: "auto" } },
            }}
            renderOption={(props, option: any) => (
              <Box component="li" {...props} sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <Typography fontSize={13}>{option.productName}</Typography>
                <Typography fontSize={11} color="#9CA3AF">Stock: {option.current_stock ?? "—"}</Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search products..."
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, "& fieldset": { borderColor: "#E5E7EB" }, pointerEvents: "auto" } }}
              />
            )}
          />
          {mergedOptions.length === 0 && (
            <Typography sx={{ fontSize: 11, color: "#dc2626", mt: 0.5 }}>
              No products loaded — close this dialog and try again.
            </Typography>
          )}
        </Box>

        {/* Current stock indicator */}
        {form.product && (
          <Box sx={{ bgcolor: "#F9FAFB", borderRadius: 2, p: 2, display: "flex", justifyContent: "space-between" }}>
            <Typography fontSize={12} color="#6B7280">Current Stock</Typography>
            <Typography fontSize={14} fontWeight={700} color="#111827">{currentStock}</Typography>
          </Box>
        )}

        {/* Type toggle */}
        <Box>
          <Typography fontSize={12} fontWeight={500} color="#6B7280" mb={0.5}>Adjustment Type</Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {(["deduct", "add"] as const).map(t => (
              <Button key={t} variant={form.type === t ? "contained" : "outlined"} disableElevation
                startIcon={t === "add" ? <AddIcon /> : <RemoveIcon />}
                onClick={() => setForm(f => ({ ...f, type: t }))}
                sx={{
                  flex: 1, borderRadius: 2, textTransform: "none", fontWeight: 600, fontSize: 13, py: 1,
                  ...(form.type === t ? {
                    bgcolor: t === "add" ? "#059669" : "#DC2626", color: "#fff",
                    "&:hover": { bgcolor: t === "add" ? "#047857" : "#B91C1C" }
                  } : {
                    borderColor: "#E5E7EB", color: "#374151",
                    "&:hover": { borderColor: "#9CA3AF" }
                  })
                }}>
                {t === "add" ? "Add Stock" : "Deduct Stock"}
              </Button>
            ))}
          </Box>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography fontSize={12} fontWeight={500} color="#6B7280" mb={0.5}>Quantity</Typography>
            <TextField fullWidth size="small" type="number" value={form.qty}
              onChange={e => setForm(f => ({ ...f, qty: e.target.value }))}
              inputProps={{ min: 0, step: 1 }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, "& fieldset": { borderColor: "#E5E7EB" } } }} />
          </Grid>
          <Grid item xs={6}>
            <Typography fontSize={12} fontWeight={500} color="#6B7280" mb={0.5}>Reason</Typography>
            <Select fullWidth size="small" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              sx={{ borderRadius: 2, "& fieldset": { borderColor: "#E5E7EB" } }}>
              {REASONS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </Select>
          </Grid>
        </Grid>

        {/* Preview */}
        {form.product && form.qty && Number(form.qty) > 0 && (
          <Box sx={{ bgcolor: form.type === "add" ? "#F0FDF4" : "#FEF2F2", borderRadius: 2, p: 2, border: `1px solid ${form.type === "add" ? "#BBF7D0" : "#FECACA"}` }}>
            <Typography fontSize={12} color="#6B7280" mb={0.5}>Stock Preview</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography fontSize={16} fontWeight={600} color="#6B7280">{currentStock}</Typography>
              <Typography fontSize={14} color={form.type === "add" ? "#059669" : "#DC2626"} fontWeight={700}>
                {form.type === "add" ? "+" : "-"}{form.qty}
              </Typography>
              <Typography fontSize={14} color="#6B7280">=</Typography>
              <Typography fontSize={16} fontWeight={700} color="#111827">
                {Math.max(0, currentStock + (form.type === "add" ? Number(form.qty) : -Number(form.qty)))}
              </Typography>
            </Box>
          </Box>
        )}

        <TextField size="small" label="Notes" multiline rows={2} value={form.notes}
          onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, "& fieldset": { borderColor: "#E5E7EB" } } }} />
      </Box>

      <Box sx={{ px: 3, py: 2, bgcolor: "#F9FAFB", borderTop: "1px solid #E5E7EB", display: "flex", gap: 1, justifyContent: "flex-end" }}>
        <Button variant="outlined" size="small" onClick={onClose}
          sx={{ borderRadius: 1.5, textTransform: "none", borderColor: "#D1D5DB", color: "#374151" }}>Cancel</Button>
        <Button variant="contained" disableElevation size="small" onClick={handleSubmit}
          disabled={saving || !form.product || !form.qty || Number(form.qty) <= 0}
          startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 14 }} />}
          sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600, bgcolor: "#0f172a", "&:hover": { bgcolor: "#1e293b" } }}>
          {saving ? "Saving..." : "Apply Adjustment"}
        </Button>
      </Box>
    </Dialog>
  );
};

export default StockAdjustmentDialog;
